// Database Connection Types
export interface DatabaseConnection {
  id?: string;
  alias: string;
  connection_uri?: string;
  use_ssh?: boolean;
  ssh_settings?: SSHSettings;
  database?: string;
  schemas?: string[];
  llm_api_key?: string;
  metadata?: Record<string, any>;
  created_at?: string;
}

export interface SSHSettings {
  host: string;
  port?: number;
  username: string;
  password?: string;
  private_key?: string;
  passphrase?: string;
}

export type DatabaseType =
  | 'POSTGRESQL'
  | 'MYSQL'
  | 'SQLSERVER'
  | 'BIGQUERY'
  | 'SNOWFLAKE'
  | 'DATABRICKS'
  | 'CLICKHOUSE'
  | 'AWSATHENA'
  | 'DUCKDB'
  | 'SQLITE'
  | 'REDSHIFT';

// Table Description Types
export interface TableDescription {
  id: string;
  db_connection_id: string;
  table_name: string;
  schema_name?: string;
  description?: string;
  columns?: ColumnDescription[];
  status: TableDescriptionStatus;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at?: string;
}

export interface ColumnDescription {
  name: string;
  data_type: string;
  description?: string;
  is_primary_key?: boolean;
  is_foreign_key?: boolean;
  foreign_key_references?: string;
}

export type TableDescriptionStatus =
  | 'NOT_SCANNED'
  | 'SYNCHRONIZING'
  | 'SCANNED'
  | 'DEPRECATED'
  | 'FAILED';

// Prompt Types
export interface Prompt {
  id: string;
  text: string;
  db_connection_id: string;
  schemas?: string[];
  metadata?: Record<string, any>;
  created_at: string;
}

export interface PromptRequest {
  text: string;
  db_connection_id: string;
  schemas?: string[];
  metadata?: Record<string, any>;
}

// SQL Generation Types
export interface SQLGeneration {
  id: string;
  prompt_id: string;
  finetuning_id?: string;
  status: SQLGenerationStatus;
  completed_at?: string;
  sql?: string;
  tokens_used?: number;
  confidence_score?: number;
  error?: string;
  intermediate_steps?: any[];
  metadata?: Record<string, any>;
  created_at: string;
}

export interface SQLGenerationRequest {
  finetuning_id?: string;
  low_latency_mode?: boolean;
  llm_config?: LLMConfig;
  evaluate?: boolean;
  sql?: string;
  metadata?: Record<string, any>;
}

export type SQLGenerationStatus = 'VALID' | 'INVALID' | 'NONE';

export interface LLMConfig {
  llm_name?: string;
  api_base?: string;
}

// NL Generation Types
export interface NLGeneration {
  id: string;
  sql_generation_id: string;
  text?: string;
  tokens_used?: number;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface NLGenerationRequest {
  llm_config?: LLMConfig;
  max_rows?: number;
  metadata?: Record<string, any>;
}

// Golden SQL Types
export interface GoldenSQL {
  id: string;
  db_connection_id: string;
  prompt_text: string;
  sql: string;
  schemas?: string[];
  metadata?: Record<string, any>;
  created_at: string;
}

// Instruction Types
export interface Instruction {
  id: string;
  db_connection_id: string;
  instruction: string;
  metadata?: Record<string, any>;
  created_at: string;
}

// Query Execution Types
export interface QueryResult {
  columns: string[];
  rows: any[][];
  row_count: number;
}

// API Response Types
export interface APIResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

// Application Config
export interface AppConfig {
  engineUrl: string;
  openaiApiKey: string;
  mongoDbUri: string;
  encryptKey: string;
}

// Pagination
export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

// Conversation Types
export type ConversationStatus = 'ACTIVE' | 'ARCHIVED' | 'DELETED';
export type MessageRole = 'USER' | 'ASSISTANT' | 'SYSTEM' | 'ERROR';

export enum MessageType {
  QUERY = 'QUERY',
  SQL = 'SQL',
  RESULT = 'RESULT',
  ERROR = 'ERROR',
  INFO = 'INFO',
  WARNING = 'WARNING',
  EXPLANATION = 'EXPLANATION'
}

export interface Conversation {
  id: string;
  user_id: string;
  db_connection_id: string;
  title: string;
  status: ConversationStatus;
  last_message_at?: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: MessageRole;
  message_type: MessageType;
  content: string;
  prompt_id?: string;
  sql_generation_id?: string;
  nl_generation_id?: string;
  query_result?: QueryResult;
  explanation?: string;
  error?: string;
  created_at: string;
  metadata?: Record<string, any>;
}

export interface ConversationCreateRequest {
  db_connection_id: string;
  title?: string;
  initial_message?: string;
  metadata?: Record<string, any>;
}

export interface ConversationUpdateRequest {
  title?: string;
  status?: ConversationStatus;
  metadata?: Record<string, any>;
}

export interface MessageCreateRequest {
  content: string;
  message_type?: MessageType;
  metadata?: Record<string, any>;
}

export interface ConversationSearchRequest {
  query?: string;
  db_connection_id?: string;
  status?: ConversationStatus;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

// ERD (Entity Relationship Diagram) Types
export interface TableNode {
  id: string;
  table_name: string;
  schema_name?: string;
  column_count: number;
  primary_keys: string[];
  description?: string;
  position?: { x: number; y: number };
}

export interface RelationshipEdge {
  id: string;
  source_table: string;
  source_schema?: string;
  source_column: string;
  target_table: string;
  target_schema?: string;
  target_column: string;
  constraint_name?: string;
  relationship_type: string;
}

export interface ERDGraph {
  id?: string;
  db_connection_id: string;
  schema_name?: string;
  nodes: TableNode[];
  edges: RelationshipEdge[];
  created_at?: string;
  layout_algorithm: string;
}

// Analytics Types
export type WidgetType = 'chart' | 'metric' | 'table' | 'markdown';
export type ChartType = 'line' | 'bar' | 'area' | 'pie' | 'scatter' | 'radar' | 'mixed';

export interface LayoutConfig {
  x: number;
  y: number;
  w: number;
  h: number;
  min_w?: number;
  min_h?: number;
}

export interface VisualizationConfig {
  chart_type: ChartType;
  x_axis: string;
  y_axis: string[];
  colors?: string[];
  labels?: Record<string, string>;
  show_legend?: boolean;
}

export interface Widget {
  id: string;
  dashboard_id: string;
  db_connection_id: string;
  title: string;
  type: WidgetType;
  query_id?: string;
  sql_query?: string;
  layout: LayoutConfig;
  viz_config?: VisualizationConfig;
  refreshed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Dashboard {
  id: string;
  organization_id: string;
  owner_id: string;
  title: string;
  description?: string;
  tags: string[];
  is_public: boolean;
  is_archived: boolean;
  theme?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDashboardRequest {
  title: string;
  description?: string;
  tags?: string[];
  is_public?: boolean;
  theme?: string;
}

export interface CreateWidgetRequest {
  title: string;
  type: WidgetType;
  db_connection_id: string;
  query_id?: string;
  sql_query?: string;
  layout: LayoutConfig;
  viz_config?: VisualizationConfig;
}

// Semantic Layer Types
export type AggregationType = 'SUM' | 'AVG' | 'COUNT' | 'MIN' | 'MAX' | 'COUNT_DISTINCT';
export type DataType = 'STRING' | 'INTEGER' | 'FLOAT' | 'DATE' | 'TIMESTAMP' | 'BOOLEAN';

export interface Dimension {
  name: string;
  description?: string;
  table_name: string;
  column_name: string;
  data_type: DataType;
  sql_expression?: string;
}

export interface Metric {
  name: string;
  description?: string;
  table_name: string;
  column_name: string;
  aggregation: AggregationType;
  sql_expression?: string;
  format?: string;
}

export interface SemanticLayerDefinition {
  id?: string;
  db_connection_id: string;
  dimensions: Dimension[];
  metrics: Metric[];
  created_at: string;
  updated_at: string;
}